import dayjs from 'dayjs'
import { ref, shallowRef, triggerRef } from 'vue'

import { NotifyEventData } from '@/shared/components/notifications'
import { useNotification } from '@/shared/composables/use-notification'
import { SIP_SUBSCRIBE_EXPIRES_DEFAULT_SECONDS } from '@/shared/services/jssip'
import { isWindowHidden } from '@/shared/services/window-visibility'
import { isStageOrDevelopment } from '@/shared/utils/import-meta'

import { CallNotificationType, CallNotificationValue } from '..'

export const notifications = shallowRef<Map<CallNotificationValue['eventData']['callId'], CallNotificationValue>>(new Map())
export const notificationsHistory = ref<Array<Partial<CallNotificationValue> & { action: '+' | '-', callId: string }>>([])

let cleanupIntervalId: NodeJS.Timeout

const removeById = (id: string): void => {
  useNotification().closePickupCallNotification(id)
  if (isStageOrDevelopment()) {
    notificationsHistory.value.push({ callId: id, timestamp: dayjs().unix(), action: '-' })
  }
  if (notifications.value.has(id)) {
    notifications.value.delete(id)
    triggerRef(notifications)
  }
}

export const cleanupExpiredNotifications = (): void => {
  const currentTimestamp = dayjs().unix()
  const expiredIds = Array.from(notifications.value.entries())
    .filter(([, { timestamp }]) => (currentTimestamp - timestamp) > SIP_SUBSCRIBE_EXPIRES_DEFAULT_SECONDS)
    .map(([id]) => id)

  if (expiredIds.length > 0) {
    expiredIds.forEach(removeById)
  }
}

export const useCallsNotifications = () => {
  if (!cleanupIntervalId) {
    cleanupIntervalId = setInterval(
      cleanupExpiredNotifications,
      SIP_SUBSCRIBE_EXPIRES_DEFAULT_SECONDS * 1000,
    )
  }
  const add = (value: NotifyEventData, type: CallNotificationType = 'pickup'): void => {
    const notification = {
      type,
      eventData: value,
      timestamp: dayjs().unix(),
    }
    notifications.value.set(value.callId, notification)
    isWindowHidden().then((hidden) => {
      if (hidden) {
        useNotification().createPickupCallNotification(value.fromName, value.targetName, value.from, value.callId)
      }
    })
    if (isStageOrDevelopment()) {
      notificationsHistory.value.push({ ...notification, callId: value.callId, action: '+' })
    }
    triggerRef(notifications)
  }

  const remove = (id: string) => {
    removeById(id)
  }

  const removeBySubscriberNumber = (number: string) => {
    Array.from(notifications.value.keys()).forEach((id) => {
      const notification = notifications.value.get(id)
      if (!notification) {
        return
      }
      const { from, target } = notification.eventData
      if (from === number || target === number) {
        removeById(id)
      }
    })
  }

  const clear = () => {
    notifications.value.clear()
    triggerRef(notifications)

    notificationsHistory.value = []
  }

  return {
    add,
    remove,
    removeBySubscriberNumber,
    clear,
  }
}
